import { Link } from 'gatsby';
import React from 'react';
import { ArrowRight, GitMerge, Target, Zap } from 'lucide-react';
import styled from '../../styled';
import { Headline } from '../Headline';
import { Reveal } from '../motion';
import { NewBadge } from '../NewBadge';
import { CommerceLoopPanel } from './CommerceLoopPanel';

const SectionWrapper = styled('section')`
  background: linear-gradient(180deg, #fff 0%, #f4f8fc 12%, #f4f8fc 88%, #fff 100%);
  background-color: #f4f8fc;
  position: relative;
  padding: 100px 0 80px;

  /* Subtle dot grid, faded at the edges */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(14, 28, 43, 0.07) 1px,
      transparent 1px
    );
    background-size: 22px 22px;
    mask-image: radial-gradient(
      ellipse 80% 70% at 50% 45%,
      black 30%,
      transparent 100%
    );
    pointer-events: none;
  }

  > * {
    position: relative;
  }

  @media (max-width: 960px) {
    padding: 72px 0 56px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  margin-bottom: 16px;
  padding: 0 24px;
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin-top: 16px;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
`;

const SubsectionHeader = styled('div')`
  text-align: center;
  margin-top: 40px;
  padding: 0 24px;
`;

const LinkCards = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 1000px;
  margin: 32px auto 0;
  padding: 0 24px;

  /* Stretch the reveal wrappers so all cards share the same height */
  > * {
    height: 100%;
    min-width: 0;
  }

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 0 16px;
  }
`;

const LinkCard = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  box-sizing: border-box;
  padding: 24px 22px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  text-decoration: none;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
  transition: border-color 0.25s ease, box-shadow 0.25s ease,
    transform 0.25s ease;

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 8px 30px rgba(24, 144, 255, 0.12);
    transform: translateY(-2px);
  }

  h3 {
    font-size: 19px;
    font-weight: 700;
    color: #0e1c2b;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    font-size: 14px;
    line-height: 1.55;
    color: #64748b;
    margin: 0;
    flex: 1;
  }
`;

const CardHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const IconChip = styled('span')`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: #e6f7ff;
  color: #1890ff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepNumber = styled('span')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: #cbd5e1;
`;

const CardLink = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1890ff;
`;

const CardFooter = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
`;

export const CommerceLoopSection = () => {
  return (
    <SectionWrapper id="publisher-solutions">
      <SectionHeader>
        <Reveal>
          <Headline component="h2">Publisher Solutions</Headline>
          <SectionDescription>
            Affilimate turns your links, pages, and placements into one
            commercial record, then gives your team the tools to act on it:
            unified data, content optimization, and sponsorship campaigns.
          </SectionDescription>
        </Reveal>
      </SectionHeader>

      <CommerceLoopPanel />

      <SubsectionHeader>
        <Reveal>
          <Headline component="h2">Grow through every stage of commerce.</Headline>
          <SectionDescription>
            Use one product or combine them as you grow.
          </SectionDescription>
        </Reveal>
      </SubsectionHeader>

      <LinkCards>
        <Reveal>
          <LinkCard to="/product/unify/">
            <CardHeader>
              <IconChip>
                <GitMerge size={20} />
              </IconChip>
              <StepNumber>01</StepNumber>
            </CardHeader>
            <h3>Unify</h3>
            <p>
              Your data foundation. Transactions, partnerships, and rates from
              100+ networks, normalized and available via API, MCP, and data
              warehouse connectors.
            </p>
            <CardFooter>
              <CardLink>
                Learn more <ArrowRight size={16} />
              </CardLink>
            </CardFooter>
          </LinkCard>
        </Reveal>

        <Reveal delay={80}>
          <LinkCard to="/product/optimize/">
            <CardHeader>
              <IconChip>
                <Target size={20} />
              </IconChip>
              <StepNumber>02</StepNumber>
            </CardHeader>
            <h3>Optimize</h3>
            <p>
              Earn more from content you&apos;ve already published. Heatmaps,
              channel and email tracking, and custom dimensions show what&apos;s
              working, down to the individual placement.
            </p>
            <CardFooter>
              <CardLink>
                Learn more <ArrowRight size={16} />
              </CardLink>
            </CardFooter>
          </LinkCard>
        </Reveal>

        <Reveal delay={160}>
          <LinkCard to="/product/amplify/">
            <CardHeader>
              <IconChip>
                <Zap size={20} />
              </IconChip>
              <StepNumber>03</StepNumber>
            </CardHeader>
            <h3>
              Amplify
              <NewBadge>New</NewBadge>
            </h3>
            <p>
              Sell and prove more sponsored content campaigns. Plan deals,
              track delivery in flight, and report results your clients trust.
              Then grow your sellable inventory with AI-powered link swaps.
            </p>
            <CardFooter>
              <CardLink>
                Learn more <ArrowRight size={16} />
              </CardLink>
            </CardFooter>
          </LinkCard>
        </Reveal>
      </LinkCards>
    </SectionWrapper>
  );
};
